import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applicationAPI } from '../services/api';
import {
  CheckCircle,
  ClipboardCheck,
  Clock,
  CreditCard,
  FileText,
  Hash,
  Loader2,
  MessageSquare,
  Phone,
  User as UserIcon,
  XCircle
} from 'lucide-react';
import { AdminModalSurface } from './AdminModal';
import { useAuthStore } from '../store/authStore';

const OPEN_REVIEW_STATUSES = ['Pending', 'Communicating'];
const STATUS_QUERY_BY_TAB = {
  Pending: OPEN_REVIEW_STATUSES.join(','),
  Approved: 'Approved',
  Rejected: 'Rejected'
};

const toText = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const firstText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return '';
};

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const getDocuments = (application = {}) => {
  if (isPlainObject(application.documents)) return application.documents;
  if (typeof application.documents === 'string') {
    try {
      const parsed = JSON.parse(application.documents);
      return isPlainObject(parsed) ? parsed : {};
    } catch (error) {
      return {};
    }
  }
  return {};
};

const getBankAccount = (documents = {}) => {
  const bankAccount = isPlainObject(documents.bankAccount)
    ? documents.bankAccount
    : isPlainObject(documents.bankDetails)
      ? documents.bankDetails
      : {};

  return {
    bankName: firstText(bankAccount.bankName, documents.bankName),
    accountNumber: firstText(
      bankAccount.accountNumber,
      bankAccount.bankAccountNumber,
      documents.bankAccountNumber,
      documents.accountNumber
    ),
    accountHolder: firstText(
      bankAccount.accountHolder,
      bankAccount.bankAccountHolder,
      documents.bankAccountHolder,
      documents.accountHolder
    )
  };
};

const getApplicationId = (application = {}) => application.id || application._id;

const getApplicantDisplayName = (application = {}) => {
  const documents = getDocuments(application);
  const bankAccount = getBankAccount(documents);

  return firstText(
    application.applicantDisplayName,
    application.applicant?.displayName,
    application.applicant?.name,
    documents.fullName,
    application.applicant?.nickname,
    documents.name,
    documents.applicantName,
    documents.identityName,
    bankAccount.accountHolder,
    application.applicantPhoneNumber,
    application.applicant?.phoneNumber,
    documents.phoneNumber,
    documents.phone,
    application.applicant?.email
  );
};

const getApplicantPhone = (application = {}) => {
  const documents = getDocuments(application);
  return firstText(
    application.applicantPhoneNumber,
    application.applicant?.phoneNumber,
    documents.phoneNumber,
    documents.phone,
    documents.contactPhone,
    documents.mobile,
    documents.whatsapp
  );
};

const getListParams = (tabKey) => (
  tabKey === 'All' ? {} : { status: STATUS_QUERY_BY_TAB[tabKey] || tabKey }
);

const isReviewable = (application = {}) => OPEN_REVIEW_STATUSES.includes(application.status);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

export default function ApplicationReview() {
  const { t } = useTranslation();
  const currentUser = useAuthStore((state) => state.user);
  const canSeeSuperAdminApplications = currentUser?.role === 'Super-Admin';
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [showCommLogModal, setShowCommLogModal] = useState(false);
  const [commLogContent, setCommLogContent] = useState('');
  const [submittingLog, setSubmittingLog] = useState(false);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await applicationAPI.getApplications(getListParams(activeTab));
      const nextApplications = (res.data.data || []).filter((item) => (
        canSeeSuperAdminApplications || item?.type !== 'Super-Admin'
      ));
      setApplications(nextApplications);
      setSelectedApp((current) => {
        if (!current) return null;
        const currentId = getApplicationId(current);
        return nextApplications.find((item) => getApplicationId(item) === currentId) || null;
      });
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, canSeeSuperAdminApplications]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const refreshCurrentList = async () => {
    const res = await applicationAPI.getApplications(getListParams(activeTab));
    const nextApplications = (res.data.data || []).filter((item) => (
      canSeeSuperAdminApplications || item?.type !== 'Super-Admin'
    ));
    setApplications(nextApplications);
    return nextApplications;
  };

  const handleReview = async (id, status) => {
    try {
      await applicationAPI.reviewApplication(id, {
        status,
        comment: reviewNote,
        expectedVersion: selectedApp?.version
      });
      setSelectedApp(null);
      setReviewNote('');
      loadApplications();
    } catch (error) {
      alert(t('applicationReview.reviewFailed') + (error.response?.data?.message || error.message));
    }
  };

  const handleAddCommLog = async () => {
    if (!commLogContent.trim()) {
      alert(t('applicationReview.enterCommunicationContent', 'Please enter communication content'));
      return;
    }

    try {
      setSubmittingLog(true);
      await applicationAPI.addCommunicationLog(getApplicationId(selectedApp), {
        content: commLogContent
      });
      setCommLogContent('');
      setShowCommLogModal(false);
      const nextApplications = await refreshCurrentList();
      const updated = nextApplications.find((item) => getApplicationId(item) === getApplicationId(selectedApp));
      if (updated) setSelectedApp(updated);
    } catch (error) {
      alert(t('applicationReview.addCommunicationFailed', 'Failed to add communication log: ') + (error.response?.data?.message || error.message));
    } finally {
      setSubmittingLog(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: {
        icon: Clock,
        label: t('applicationReview.statusPending'),
        className: 'border-amber-300 bg-amber-50 text-amber-800'
      },
      Communicating: {
        icon: MessageSquare,
        label: t('applicationReview.statusCommunicating'),
        className: 'border-blue-300 bg-blue-50 text-blue-800'
      },
      Approved: {
        icon: CheckCircle,
        label: t('applicationReview.statusApproved'),
        className: 'border-emerald-300 bg-emerald-50 text-emerald-800'
      },
      Rejected: {
        icon: XCircle,
        label: t('applicationReview.statusRejected'),
        className: 'border-rose-300 bg-rose-50 text-rose-800'
      }
    }[status];

    if (!statusConfig) return null;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex min-h-7 max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${statusConfig.className}`}>
        <Icon size={13} className="shrink-0" />
        <span className="break-words">{statusConfig.label}</span>
      </span>
    );
  };

  const tabs = [
    { key: 'All', label: t('common.all') },
    { key: 'Pending', label: t('applicationReview.statusOpen', 'Open') },
    { key: 'Approved', label: t('applicationReview.statusApproved') },
    { key: 'Rejected', label: t('applicationReview.statusRejected') }
  ];

  const selectedDocuments = selectedApp ? getDocuments(selectedApp) : {};
  const selectedBankAccount = getBankAccount(selectedDocuments);
  const selectedName = selectedApp ? getApplicantDisplayName(selectedApp) : '';
  const selectedPhone = selectedApp ? getApplicantPhone(selectedApp) : '';
  const selectedIsReviewable = selectedApp ? isReviewable(selectedApp) : false;

  const renderDetailRow = (label, value, options = {}) => {
    if (!toText(value)) return null;
    return (
      <div className="grid grid-cols-1 gap-1 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] p-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:items-start">
        <span className="text-xs font-bold text-[var(--admin-text-muted)]">{label}</span>
        <span className={`min-w-0 break-words text-sm font-semibold text-[var(--admin-text-primary)] ${options.mono ? 'font-mono' : ''}`}>
          {value}
        </span>
      </div>
    );
  };

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 break-words text-2xl font-black leading-tight">
            <ClipboardCheck className="shrink-0 text-cyan-600" />
            {t('applicationReview.title')}
          </h1>
          <p className="mt-2 max-w-3xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">
            {t('applicationReview.subtitle')}
          </p>
        </div>
      </header>

      <section className="admin-muted-panel rounded-lg p-2">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`min-h-10 rounded-lg px-4 py-2 text-sm font-black transition ${
                activeTab === tab.key
                  ? 'admin-primary-button'
                  : 'admin-secondary-button hover:border-cyan-300 hover:text-cyan-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)]">
        <section className="min-w-0 space-y-3">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-10 text-center text-sm font-bold text-[var(--admin-text-muted)]">
              <Loader2 className="mx-auto mb-3 animate-spin text-cyan-600" size={24} />
              {t('applicationReview.loading')}
            </div>
          ) : applications.length === 0 ? (
            <div className="admin-muted-panel rounded-lg border-dashed p-10 text-center text-sm font-bold text-[var(--admin-text-muted)]">
              {t('applicationReview.noApplications')}
            </div>
          ) : (
            applications.map((app) => {
              const appId = getApplicationId(app);
              const displayName = getApplicantDisplayName(app) || t('applicationReview.anonymous');
              const phoneNumber = getApplicantPhone(app);
              const isSelected = getApplicationId(selectedApp || {}) === appId;

              return (
                <button
                  key={appId}
                  type="button"
                  onClick={() => setSelectedApp(app)}
                  className={`admin-card w-full rounded-lg p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-cyan-200 ${
                    isSelected ? 'border-cyan-400 bg-cyan-50/70' : 'hover:border-cyan-300'
                  }`}
                >
                  <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-base font-black text-cyan-700 ring-1 ring-cyan-200">
                        {(displayName || phoneNumber || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h3 className="break-words text-base font-black leading-6 text-[var(--admin-text-primary)]">
                          {displayName}
                        </h3>
                        <p className="mt-1 break-all font-mono text-xs text-[var(--admin-text-muted)]">
                          {phoneNumber || t('applicationReview.phoneUnavailable', 'No phone provided')}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0">{getStatusBadge(app.status)}</div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-[var(--admin-text-muted)]">
                    <span className="inline-flex max-w-full items-center gap-1 rounded-lg bg-[var(--admin-surface-soft)] px-2.5 py-1.5">
                      <UserIcon size={13} className="shrink-0 text-cyan-600" />
                      <span>{t('applicationReview.applyingFor')}</span>
                      <span className={`break-words text-[var(--admin-text-primary)] ${app.type === 'Super-Admin' ? 'text-amber-700' : ''}`}>
                        {app.type}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-lg bg-[var(--admin-surface-soft)] px-2.5 py-1.5">
                      <Clock size={13} className="shrink-0" />
                      {formatDate(app.createdAt)}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </section>

        <aside className="admin-card h-fit min-w-0 rounded-lg p-5">
          {selectedApp ? (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h2 className="flex min-w-0 items-center gap-2 break-words text-xl font-black">
                  <FileText className="shrink-0 text-cyan-600" size={21} />
                  {t('applicationReview.reviewDetails')}
                </h2>
                <div className="shrink-0">{getStatusBadge(selectedApp.status)}</div>
              </div>

              <section className="admin-muted-panel rounded-lg p-4">
                <p className="mb-3 text-xs font-black text-[var(--admin-text-muted)]">{t('applicationReview.applicantInfo')}</p>
                <div className="space-y-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <UserIcon size={15} className="mt-0.5 shrink-0 text-cyan-600" />
                    <span className="min-w-0 break-words text-sm font-black text-[var(--admin-text-primary)]">
                      {selectedName || t('applicationReview.anonymous')}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-start gap-2">
                    <Phone size={15} className="mt-0.5 shrink-0 text-[var(--admin-text-muted)]" />
                    <span className="min-w-0 break-all font-mono text-sm text-[var(--admin-text-muted)]">
                      {selectedPhone || t('applicationReview.phoneUnavailable', 'No phone provided')}
                    </span>
                  </div>
                </div>
              </section>

              <section className="space-y-3">
                <p className="text-xs font-black text-[var(--admin-text-muted)]">{t('applicationReview.documentsProof')}</p>
                {Object.keys(selectedDocuments).length > 0 ? (
                  <div className="space-y-2">
                    {renderDetailRow(t('applicationReview.fullName', 'Full Name'), selectedDocuments.fullName)}
                    {renderDetailRow(t('applicationReview.idNumber', 'ID Number'), selectedDocuments.idNumber, { mono: true })}
                    {renderDetailRow(t('applicationReview.address', 'Address'), selectedDocuments.address)}
                    {renderDetailRow(
                      (
                        <span className="inline-flex items-center gap-1">
                          <Hash size={12} />
                          {t('applicationReview.deviceNo', 'Device Number')}
                        </span>
                      ),
                      firstText(selectedDocuments.deviceNo, selectedDocuments.deviceId, selectedDocuments.requestedDeviceNo),
                      { mono: true }
                    )}
                    {(selectedBankAccount.bankName || selectedBankAccount.accountNumber || selectedBankAccount.accountHolder) && (
                      <div className="rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] p-3">
                        <p className="mb-3 inline-flex items-center gap-1 text-xs font-black text-[var(--admin-text-muted)]">
                          <CreditCard size={13} />
                          {t('applicationReview.bankAccount', 'Bank Account')}
                        </p>
                        <div className="space-y-2">
                          {renderDetailRow(t('applicationReview.bankName', 'Bank'), selectedBankAccount.bankName || '-')}
                          {renderDetailRow(t('applicationReview.bankAccountNumber', 'Account Number'), selectedBankAccount.accountNumber || '-', { mono: true })}
                          {renderDetailRow(t('applicationReview.bankAccountHolder', 'Account Holder'), selectedBankAccount.accountHolder || '-')}
                        </div>
                      </div>
                    )}
                    {renderDetailRow(t('applicationReview.experience', 'Experience'), selectedDocuments.experience)}
                    {renderDetailRow(t('applicationReview.motivation', 'Motivation'), selectedDocuments.motivation)}
                    <div className="grid grid-cols-1 gap-2">
                      {selectedDocuments.idCardUrl && (
                        <a href={selectedDocuments.idCardUrl} target="_blank" rel="noreferrer" className="admin-secondary-button rounded-lg px-3 py-2 text-sm font-bold transition hover:border-cyan-300 hover:text-cyan-700">
                          {t('applicationReview.viewIdCard')}
                        </a>
                      )}
                      {selectedDocuments.salaryProofUrl && (
                        <a href={selectedDocuments.salaryProofUrl} target="_blank" rel="noreferrer" className="admin-secondary-button rounded-lg px-3 py-2 text-sm font-bold transition hover:border-cyan-300 hover:text-cyan-700">
                          {t('applicationReview.viewSalaryProof')}
                        </a>
                      )}
                      {selectedDocuments.businessLicenseUrl && (
                        <a href={selectedDocuments.businessLicenseUrl} target="_blank" rel="noreferrer" className="admin-secondary-button rounded-lg px-3 py-2 text-sm font-bold transition hover:border-cyan-300 hover:text-cyan-700">
                          {t('applicationReview.viewBusinessLicense')}
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="admin-muted-panel rounded-lg border-dashed p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">
                    {t('applicationReview.noDocuments')}
                  </div>
                )}
              </section>

              <section className="admin-muted-panel rounded-lg p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs font-black text-[var(--admin-text-muted)]">
                    {t('applicationReview.communicationLogs', 'Communication Logs')}
                  </p>
                  {selectedIsReviewable && (
                    <button
                      type="button"
                      onClick={() => setShowCommLogModal(true)}
                      className="admin-secondary-button inline-flex min-h-9 items-center justify-center rounded-lg px-3 py-2 text-xs font-black transition hover:border-cyan-300 hover:text-cyan-700"
                    >
                      {t('applicationReview.addLog', 'Add Log')}
                    </button>
                  )}
                </div>
                {selectedApp.communicationLogs && selectedApp.communicationLogs.length > 0 ? (
                  <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                    {selectedApp.communicationLogs.map((log, idx) => (
                      <div key={`${log.createdAt || idx}-${idx}`} className="rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] p-3">
                        <div className="mb-1 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                          <span className="break-words text-xs font-black text-cyan-700">{log.adminName || '-'}</span>
                          <span className="text-xs text-[var(--admin-text-muted)]">{log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}</span>
                        </div>
                        <p className="break-words text-sm leading-6 text-[var(--admin-text-primary)]">{log.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm font-bold text-[var(--admin-text-muted)]">{t('applicationReview.noLogs', 'No communication logs yet')}</p>
                )}
              </section>

              {selectedIsReviewable ? (
                <section className="space-y-3">
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-xs font-black text-[var(--admin-text-muted)]">
                      <MessageSquare size={14} />
                      {t('applicationReview.reviewNote')}
                    </span>
                    <textarea
                      value={reviewNote}
                      onChange={(event) => setReviewNote(event.target.value)}
                      placeholder={t('applicationReview.reviewPlaceholder')}
                      className="min-h-28 w-full rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] p-3 text-sm text-[var(--admin-text-primary)] outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                    />
                  </label>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => handleReview(getApplicationId(selectedApp), 'Rejected')}
                      className="min-h-11 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-black text-rose-800 transition hover:bg-rose-100"
                    >
                      {t('applicationReview.reject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReview(getApplicationId(selectedApp), 'Approved')}
                      className="min-h-11 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-800 transition hover:bg-emerald-100"
                    >
                      {t('applicationReview.approve')}
                    </button>
                  </div>
                </section>
              ) : (
                <section className="admin-muted-panel rounded-lg p-4">
                  <p className="text-xs font-black text-[var(--admin-text-muted)]">{t('applicationReview.finalResult')}</p>
                  <div className="mt-2 font-black text-[var(--admin-text-primary)]">{selectedApp.status}</div>
                  {selectedApp.rejectionReason && (
                    <p className="mt-2 break-words text-sm font-semibold text-rose-700">
                      {t('applicationReview.reason')} {selectedApp.rejectionReason}
                    </p>
                  )}
                </section>
              )}
            </div>
          ) : (
            <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-[var(--admin-border)] p-8 text-center text-[var(--admin-text-muted)]">
              <ClipboardCheck size={46} className="mb-4 opacity-30" />
              <p className="break-words text-sm font-bold">{t('applicationReview.selectToReview')}</p>
            </div>
          )}
        </aside>
      </div>

      {showCommLogModal && (
        <AdminModalSurface onClose={() => setShowCommLogModal(false)}>
          <div className="admin-card w-full max-w-lg rounded-lg p-5" onClick={(event) => event.stopPropagation()}>
            <h3 className="mb-4 break-words text-lg font-black">{t('applicationReview.addCommLog', 'Add Communication Log')}</h3>
            <textarea
              value={commLogContent}
              onChange={(event) => setCommLogContent(event.target.value)}
              placeholder={t('applicationReview.commLogPlaceholder', 'Enter communication details...')}
              className="mb-4 min-h-32 w-full rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] p-3 text-sm text-[var(--admin-text-primary)] outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              autoFocus
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setShowCommLogModal(false);
                  setCommLogContent('');
                }}
                className="admin-secondary-button min-h-10 rounded-lg px-4 py-2 text-sm font-black transition"
                disabled={submittingLog}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddCommLog}
                className="admin-primary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60"
                disabled={submittingLog}
              >
                {submittingLog && <Loader2 size={16} className="animate-spin" />}
                {submittingLog ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </AdminModalSurface>
      )}
    </div>
  );
}
